<template>
  <div class="work-order-execution">
    <div class="work-order-execution__main">
      <div class="work-order-execution__left">
        <div class="work-order-execution__fields">
          <fields-renderer
            :config="fieldsRendererConfig"
            :form-data="formData"
            :fields-config="fieldsConfig"
            :validation-errors="validationErrors"
            :initial-autocompletes="initialAutocompletes"
            :permissions="permissions"
            :edit="false"
            :create-mode="false"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :remove-data-property-by-key="removeDataPropertyByKey"
            :replace-property-value-by-key="replacePropertyValueByKey"
          />
        </div>

        <div class="work-order-execution__history">
          <div class="work-order-execution__history-title">
            {{ translate(`${NS}.task_history`) }}
          </div>

          <ul
            v-if="taskHistory.length"
            class="work-order-execution__history-list"
          >
            <li
              v-for="(entry, index) in taskHistory"
              :key="index"
              class="work-order-execution__history-row"
            >
              <span class="work-order-execution__history-time">
                {{ entry.time }}
              </span>

              <span>{{ entry.description }}</span>
            </li>
          </ul>

          <div
            v-else
            class="work-order-execution__history-empty"
          >
            {{ translate(`${NS}.no_task_history`) }}
          </div>
        </div>
      </div>

      <div class="work-order-execution__side">
        <div class="work-order-execution__side-inner">
          <work-orders-map-panel
            :streets="streets"
            :irregularities="irregularities"
            :vehicles="vehicles"
          />
        </div>
      </div>
    </div>

    <winter-service-log-history
      :record="formData"
      :translation-namespace="NS"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import WorkOrdersMapPanel from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/components/WorkOrdersMapPanel.vue'
import WinterServiceLogHistory from '@/modules/road-maintenance-module/winter-service-v2/common/components/WinterServiceLogHistory.vue'
import { isArray } from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'
import { toDatetimeLabel } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/workOrderData'
import { WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its single `rows` entry holds
  // the field list (see useWorkOrderExecution for why it lives there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  // Declared but never honoured: the whole screen is read-only, so the renderers below
  // are handed `false` whatever the tab layout passes in. They are taken as props
  // rather than left to fall through as attributes on the root element.
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const { translate } = useTranslate()

// What the map draws - the route with its coverage, the reports along it and where the
// vehicle has got to - comes off the tab's own config: `useWorkOrderExecution` fetches it
// when the order arrives, so the loader the layout puts over the tab can stay up until
// all of it is in. The vehicle is on this tab alone: "Podaci o izvršenju" is about how
// the order actually went, and the task data tab beside it describes the plan.
const streets = computed(() => isArray(props.config?.streets) ? props.config.streets : [])
const irregularities = computed(() => isArray(props.config?.irregularities) ? props.config.irregularities : [])
const vehicles = computed(() => isArray(props.config?.vehicles) ? props.config.vehicles : [])

// One field per row, so the form reads as the single column the design shows next to
// the map.
const fieldsRendererConfig = computed(() => {
  const [fieldsRow] = props.config?.rows || []

  return {
    columnsCount: fieldsRow?.columnsCount || 1,
    fields: fieldsRow?.fields || []
  }
})

/**
 * What happened while the order was driven - it started, it entered the task, it was
 * warned about leaving the route. The backend does not report it yet, so the block
 * says so rather than showing an empty box, and picks the entries up as soon as
 * `execution.history` starts arriving.
 */
const taskHistory = computed(() => {
  const history = props.formData?.execution?.history

  if (!isArray(history)) {
    return []
  }

  return history.map(entry => ({
    time: toDatetimeLabel(entry?.timestamp ?? entry?.created_at) || '',
    description: entry?.description ?? entry?.message ?? ''
  }))
})
</script>

<style scoped>
.work-order-execution {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.work-order-execution__main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  align-items: start;
}

.work-order-execution__left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.work-order-execution__fields,
.work-order-execution__history {
  padding: 12px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

.work-order-execution__history-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.work-order-execution__history-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.work-order-execution__history-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.work-order-execution__history-time {
  white-space: nowrap;
  color: #6B7280;
}

.work-order-execution__history-empty {
  font-size: 13px;
  color: #6B7280;
}

/* The map keeps its own height rather than following the column beside it, which is
   shorter here than on the task tab. */
.work-order-execution__side {
  min-width: 0;
  height: 480px;
}

.work-order-execution__side-inner {
  height: 100%;
}

@media (max-width: 960px) {
  .work-order-execution__main {
    grid-template-columns: minmax(0, 1fr);
  }

  .work-order-execution__side {
    height: 320px;
  }
}
</style>
